import { formatCurrencyValue } from "../../../util/PaymentUtils";
import { formatFrequency } from "@securspace/securspace-ui-kit";
import { assetTypeOptions, frequencyOptions, BookingStatusGroups } from "../data";
import { formatEndDateDisplay } from "../../../util/DateUtils";

import moment from "moment";
import { Booking } from "../../../types/Booking";

const sortByFields = [
  {label: "Booking Number", value: "ORDER_NUMBER"},
  {label: "Buyer", value: "BUYER_COMPANY_NAME"},
  {label: "Start Date", value: "START_DATE"}
];

const filterBySelectOption = [
  {
    label: "Equipment Type",
    value: "",
    name: "equipmentType",
    option: assetTypeOptions
  },
  {
    label: "Frequency",
    value: "",
    name: "frequency",
    option: frequencyOptions
  }
];

const supplierHeaderCells = [
  { id: 'orderNumber', label: 'BOOKING NUMBER' },
  { id: 'buyerCompanyName', label: 'Buyer' },
  { id: 'startDateValue', label: 'Start Date' },
  { id: 'endDateValue', label: 'End Date' },
  { id: 'numberOfSpaces', label: 'Number of Spaces' },
  { id: 'locationName', label: 'Location' },
  { id: 'brokered', label: 'Brokered' },
  { id: 'frequencyValue', label: 'Frequency' },
  { id: 'assetType', label: 'Equipment Type' },
  { id: 'netRate', label: 'Net Booking Rate' },
  { id: 'overageRate', label: 'Overage Rate' },
  { id: 'status', label: 'STATUS' },
  { id: 'creationDate', label: 'Created On' },
];

// overageRate is resolved by the API for the booking (equipment type rate, falling back to the location rate).
function formatOverageRateField(booking?: Booking) {
  // The booking's own flag decides: it can charge overages after the location turns them off.
  if (booking?.chargeOverages) {
    if (booking.brokered && Number.isInteger(booking.brokeredSupplierOverageRatePaid)) {
      return formatCurrencyValue(booking.brokeredSupplierOverageRatePaid, true);
    }
    if (typeof booking.overageRate === 'number' && Number.isInteger(booking.overageRate)) {
      return formatCurrencyValue(booking.overageRate, true);
    }
  }

  return '';
}

// The display fields replace the raw numeric fields of the same name with formatted strings.
type DisplayFields = 'initialSupplierPayoutAmount' | 'recurringSupplierPayoutAmount' | 'supplierCompanyName'
  | 'active' | 'brokered' | 'rate' | 'overageRate' | 'netRate';

export type TransformedSupplierBooking = Omit<Booking, DisplayFields> & {
  initialSupplierPayoutAmount: string | number;
  recurringSupplierPayoutAmount: string | number;
  frequencyValue: string;
  startDateValue: string;
  supplierCompanyName: string;
  active: string;
  brokered: string;
  endDateValue: string;
  rate: string | number;
  overageRate: string | number;
  netRate: string;
};

function transformBooking(booking: Booking): TransformedSupplierBooking {
  const commonProperties = {
    initialSupplierPayoutAmount: formatCurrencyValue(booking.initialCharge, true),
    recurringSupplierPayoutAmount: formatCurrencyValue(booking.recurringSupplierPayoutAmount, true),
    frequencyValue: formatFrequency(booking.frequency, booking.durationType),
    startDateValue: moment(booking.startDate).format("L h:mm a").toUpperCase(),
    supplierCompanyName: booking.supplierCompanyName ? booking.supplierCompanyName : 'N/A',
    active: booking.active ? 'Active' : 'Not Active',
    brokered: booking.brokered ? 'True' : 'False',
    endDateValue: formatEndDateDisplay(booking?.endDate, "L h:mm A"),
    rate: booking.brokered ? 'N/A' : formatCurrencyValue(booking.rate) + (booking.durationType === 'WEEKLY' ? ' /week' : booking.durationType === 'MONTHLY' ? ' /month' : ' /day'),
    overageRate: formatOverageRateField(booking),
    netRate: formatCurrencyValue(booking.netRate) + (booking.durationType === 'WEEKLY' ? ' /week' : booking.durationType === 'MONTHLY' ? ' /month' : ' /day'),
  };

  return {
    ...booking,
    ...commonProperties
  };
}

const SupplierBookingTabs = Object.freeze({
  ACTIVE: BookingStatusGroups.ACTIVE,
  PENDING: BookingStatusGroups.PENDING,
  INACTIVE: BookingStatusGroups.INACTIVE,
  FAILED: BookingStatusGroups.FAILED,
});

export {
    sortByFields,
    supplierHeaderCells,
    filterBySelectOption,
    transformBooking,
    SupplierBookingTabs
}
